"use client";

import { IconX } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { LegendItem } from "@/components/Layout/LegendItem";
import { ToggleSwitch } from "@/components/ui/ToggleSwitch";
import { useProjectText } from "@/features/project/useProjectText";
import type { SecondaryLayer } from "@/types";
import { swatchLayers } from "../legendSwatches";
import { useSecondaryLayers } from "../useSecondaryLayers";

/**
 * One toggleable overlay: its swatch, its name, and the switch.
 *
 * A layer the project doesn't allow switching isn't listed — the row would be a
 * name beside a control that refuses to move.
 *
 * Where the tenant wrote an explanation, an info button reveals it in place.
 * Legacy hung that on an `<i onClick>`, as it did the button that closes it
 * again, so neither could be reached from a keyboard.
 */
export function SecondaryLayerRow({ layer }: { layer: SecondaryLayer }) {
  const t = useTranslations();
  const projectText = useProjectText();
  const { isActive, toggle } = useSecondaryLayers();
  const [showInfo, setShowInfo] = useState(false);

  if (!layer.canBeToggled || !layer.name) return null;

  const label = projectText(`map.layers.${layer.name}`, layer.name);

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between">
        <span className="align-middle text-sm font-bold">
          <LegendItem styleLayers={swatchLayers(layer)}>
            {layer.isIcon && (
              <i className={`icon ic-jammaster_layers-f ic-${layer.icon} mr-1`} />
            )}
            {label}
            {layer.isInfoButton && (
              <button
                type="button"
                onClick={() => setShowInfo(!showInfo)}
                aria-expanded={showInfo}
                aria-label={`${label} — ${t("secondaryLayer.info")}`}
                className="icon ic-jammaster_info-f ml-1 cursor-pointer text-black dark:text-off-white"
              />
            )}
          </LegendItem>
        </span>

        <div className="flex h-6 w-11 items-center justify-end rounded-full">
          {layer.downloadUrl && (
            <a
              href={layer.downloadUrl}
              target="_blank"
              rel="noreferrer"
              aria-label={`${label} — ${t("secondaryLayer.download")}`}
              className="icon ic-material_ic_file_download_48px mr-1 cursor-pointer hover:text-primary"
            />
          )}

          <ToggleSwitch
            isActive={isActive(layer.name)}
            onChange={() => toggle(layer.name as string)}
            label={label}
          />
        </div>
      </div>

      {showInfo && layer.infoText && (
        <div className="mt-2.5 flex items-center justify-between bg-primary-opacity-25 p-[10px] text-xs font-bold">
          {projectText(`map.layers.${layer.infoText}`, layer.infoText)}
          <button
            type="button"
            onClick={() => setShowInfo(false)}
            aria-label={t("secondaryLayer.closeInfo")}
            className="ml-2 cursor-pointer"
          >
            <IconX size={12} aria-hidden />
          </button>
        </div>
      )}
    </div>
  );
}
